import React, { useEffect, useState } from "react";
import cx from "classnames";
import { COPY_STRINGS } from "../../../constants/translatedCopy";
import usePauseControl from "../../Reader/Phrase/hooks/usePauseControl";
import usePrevious from "../../Reader/Phrase/hooks/usePrevious";
import { getRandomArrayElement } from "../../../library";
import { getUserData } from '../../../services/API';
import {logSessionInfo} from '../../../services/logger';
import { listen } from "../../../services/Listen";
import { useSelector } from "react-redux";
import { selectReaderManager } from "../../Reader/ReaderManagerEvents/ReaderManagerSlice";
import { READING_MODES } from "../../../constants/constants";

export default function SingleWord(props) {

  const { contextStates } = useSelector(selectReaderManager)
  const { readingMode } = contextStates

  // constants
  const hintsEnabled = readingMode === READING_MODES.TUTOR && getUserData().locale === "en-us" ? true : false; //TODO: get this from entitlements

  // state
  const [attemptNumber, setAttemptNumber] = useState(0);
  const [imageDisplayed, setImageDisplayed] = useState(!hintsEnabled); // start out with the image displayed if we're not using it as a hint
  const prevAttemptNumber = usePrevious(attemptNumber);


  // props
  const {
    paused,
    setWillHandlePause,
    startPhrase,
    pausePhrase,
    resumePhrase,
    doneHandlingPause,
    transcript,
    speech,
    currentPageIndex,
    setDisableInterventionsForPhrase,
    setAllowFirstWordSkipOnPause = () => {},
    triggerWordIntervention,
    wordMetadata,
  } = props;

  // if the user is configured to have hints enabled based on their entitlements,
  //   use the usePauseControl hook to take control of pause handling.
  //   Otherwise, let ReaderManager do its thing
  const {
    shouldHandlePause,
    finishHandlePause,
    passPauseControlBackToReaderManager,
  } = usePauseControl({
    paused,
    setWillHandlePause,
    pausePhrase,
    resumePhrase,
    doneHandlingPause,
    pauseControlDisabled: !hintsEnabled, //this makes usePauseControl do nothing when hints are disabled
  });

  // if hints are enabled, disable phrase time management on mount. This disables the phrase timeout and the start reading timer
  useEffect(() => {
    if(hintsEnabled){
      setDisableInterventionsForPhrase(false);
    }
  }, []);

  useEffect(() => {
    // if the phrase is paused, and we should handle the pause, and we haven't handled the pause yet
    if (hintsEnabled && paused && shouldHandlePause && attemptNumber == 0) {
      handlePause();
    }
  }, [paused]);

  useEffect(() => {
    // return a function to be used on unmount
    if(prevAttemptNumber != attemptNumber && attemptNumber != 0){
      logSessionInfo("word_list_item_scaffold_advanced", {
        attemptNumber,
        currentPageIndex
      });
    }
  }, [attemptNumber]);

  // functions
  function handlePause() {
    setAttemptNumber(attemptNumber + 1);
    // if the child attempted the word, but got it wrong, hand control back to reader manager so that we intevene if they fail again
    if (transcript && transcript.final && transcript.final != "") {
      passPauseControlBackToReaderManager();
      finishHandlePause(); // called automatically from inside reader manager if we call default
    } else {
        speech.stopListening();
        speech.speak({
          textToSpeak: getRandomArrayElement(COPY_STRINGS.wordAttackPrompt),
          onEnd: () => {
            listen({
              speech: speech,
              targetWord: props.text,
              noPatter: true,
              noRetries: true,
              callback: ((resp)=>{
                if (resp.status && resp.status == 'greatJob'){
                  passPauseControlBackToReaderManager();
                  finishHandlePause();
                  props.advanceOneWord();
                } else {
                  setImageDisplayed(true);
                  listen({
                    speech: speech,
                    targetWord: props.text,
                    noPatter: true,
                    noRetries: true,
                    callback: (resp) => {
                      setDisableInterventionsForPhrase(true);
                      // GT-400: interventions are now off, so an unrecognized first word of a story
                      // needs the forced pause skip to get past ReaderManager's first-word guard.
                      setAllowFirstWordSkipOnPause(true);
                      passPauseControlBackToReaderManager();
                      finishHandlePause();
                      if (!wordMetadata) {
                        console.warn("NO METADATA AVAILABLE FOR WORD - SKIPPING INTERVENTION:", props.text);
                        logSessionInfo("single_word_no_metadata_skip_intervention", {
                          word: props.text,
                          pageIndex: currentPageIndex
                        });
                        // Just advance without intervention
                        props.advanceOneWord();
                      } else {
                        // Metadata exists, proceed with intervention
                        triggerWordIntervention({word: props.text});
                      }
                  }})
                }
              })
            });
          }
        });
    }
  }

  // render
  let containerClasses = cx({
    exampleItemContainer: props.isInstruction,
    singleWordContainer: true,
    withImage: props.image,
    testItemContainer: true,
  });

  let wordContainerClasses = cx({
    example: props.isInstruction,
    singleWordText: true,
    outlined: !props.image,
    earlyReader: props.isEarlyReader && props.isEarlyReader() ? true : false,
  });

  return (
    <div className={containerClasses}>
      {props.isInstruction && (
        <div className="exampleIndicator">{COPY_STRINGS.example}:</div>
      )}
      {props.image && imageDisplayed && (
        <img className="singleWordImage" src={props.image} alt="" />
      )}
      <div className={wordContainerClasses}>{props.text}</div>
    </div>
  );
}
